import { useEffect, useState } from 'react'

import { v4 as uuidv4 } from 'uuid'

import useTemporaryData, {
    TemporaryDataType,
    TemporarySetDataType
} from '../temporary-data-table/useTemporaryDataState'
import { fetchAllTemporaryData } from '../importer-api'
import { fetchCodafEntity } from '../services/api'
import { fetchEntityOptions, fetchWells, fetchRigs } from '../../../widgets/static-data/api/api'

interface UseDataTableProps {
    data: TemporaryDataType
    setData: TemporarySetDataType
    liveRigs: {
        id: string
        name: string
    }[]
    liveWells: {
        id: string
        name: string
    }[]
    isFetching: boolean
    setIsFetching: (value: boolean) => void
    optionsOfWellEntity: WellSelectOptions
}

export const useDataTable = (temporaryData: any[], setTemporaryData: (value: any[]) => void): UseDataTableProps => {
    const [isFetching, setIsFetching] = useState(true)
    const [liveRigs, setLiveRigs] = useState<{ id: string; name: string }[]>([])
    const [liveWells, setLiveWells] = useState<{ id: string; name: string }[]>([])
    const [allCodafRig, setAllCodafRig] = useState([])
    const [allCodafWell, setAllCodafWell] = useState([])
    const [optionsOfWellEntity, setOptionsOfWellEntity] = useState<WellSelectOptions>(null)

    const { data, setData } = useTemporaryData()

    const getLabelFromValue = (options: SelectOptions[], value: string): string | undefined => {
        if (!options?.length) return

        return options.find(
            option =>
                option?.value?.toLowerCase() === value?.toLowerCase() ||
                option?.label?.toLowerCase() === value?.toLowerCase()
        )?.label
    }

    const mountDataTable = (
        temporary: any[],
        codafWells: any[],
        codafRigs: any[],
        wells: {
            id: string
            name: string
        }[],
        rigs: {
            id: string
            name: string
        }[]
    ): void => {
        if (temporary.length === 0) {
            setIsFetching(false)

            return
        }

        const tempWells = []
        const tempInterventions = []
        const tempSections = []
        const tempRigTables = []
        const tempRuns = []
        const tempCements = []
        const tempTubulars = []
        const tempPipeMovements = []
        const tempConnections = []
        const tempRops = []

        temporary.forEach(data => {
            let content
            try {
                if (typeof data.content === 'string' && data.content.trim() !== '') {
                    content = JSON.parse(data.content)
                } else {
                    content = {}
                }
            } catch (e) {
                console.error('Invalid JSON in data.content:', data.content, e)
                content = {}
            }
            content.parent = data.parent
            content.key = data.id
            content.rowId = data.id
            content.nodeId = data.nodeId
            content.frontUuid = uuidv4()

            if (data.entityType == 'WELL') {
                const liveWell = wells.find(
                    well =>
                        well?.name &&
                        (content?.name || content?.wellName) &&
                        well.name.toLowerCase() === (content?.name ?? content?.wellName)?.toLowerCase()
                )
                const liveRig = rigs.find(
                    rig =>
                        rig?.name &&
                        (content?.assetName || content?.rigName) &&
                        rig.name.toLowerCase() === (content?.assetName ?? content?.rigName)?.toLowerCase()
                )
                const newWell: WellType = codafWells?.find(
                    well => well?.assetId && liveWell?.id && Number(well.assetId) === Number(liveWell.id)
                )
                const newRig: RigType = codafRigs?.find(
                    rig => rig?.assetId && liveRig?.id && Number(rig.assetId) === Number(liveRig.id)
                )

                const well: WellDto = {
                    ...content,
                    assetId: newWell?.assetId ?? content?.assetId,
                    fieldName: newWell?.fieldName ?? content?.fieldName,
                    id: newWell?.id ?? content?.id,
                    latitude: newWell?.latitude ?? content?.latitude,
                    longitude: newWell?.longitude ?? content?.longitude,
                    name: newWell?.name ?? liveWell?.name ?? content?.name,
                    referenceSystem:
                        optionsOfWellEntity &&
                        getLabelFromValue(
                            optionsOfWellEntity?.referenceSystem,
                            newWell?.referenceSystem ?? content?.referenceSystem
                        ),
                    waterDepth: (newWell?.waterDepth as AttributeUnit)?.value ?? content.waterDepthValue,
                    waterDepthAsAttributeUnit: newWell?.waterDepth,
                    wellCategory:
                        optionsOfWellEntity &&
                        getLabelFromValue(
                            optionsOfWellEntity?.wellCategory,
                            newWell?.wellCategory ?? content?.wellCategory
                        ),
                    wellType:
                        optionsOfWellEntity &&
                        getLabelFromValue(optionsOfWellEntity?.wellType, newWell?.wellType ?? content?.wellType),
                    wellName: newWell?.name ?? liveWell?.name ?? content?.name,
                    wellId: newWell?.assetId ?? liveWell?.id,
                    wellIdInCodaf: newWell?.id,
                    uuid: newWell?.uuid,
                    rigName: liveRig?.name ?? content?.assetName,
                    rigId: newRig?.assetId ?? liveRig?.id,
                    rigIdInCodaf: newRig?.id,
                    frontParentUuid: undefined
                }
                tempWells.push(well)
            }
            if (data.entityType == 'INTERVENTION') {
                const frontParentUuid = tempWells.find(
                    well => well?.nodeId && content?.parent && well.nodeId === content.parent
                )?.frontUuid
                tempInterventions.push({
                    ...content,
                    tactic: content.tatic,
                    frontParentUuid: frontParentUuid ?? undefined
                })
            }
            if (data.entityType == 'SECTION') {
                const frontParentUuid = tempInterventions.find(
                    intervention => intervention?.nodeId && content?.parent && intervention.nodeId === content.parent
                )?.frontUuid
                tempSections.push({
                    ...content,
                    startMD: content.startMDValue,
                    finalMD: content.finalMDValue,
                    oD: content.oDValue,
                    prevCasingOD: content.prevCasingODValue,
                    prevCasingFinalMD: content.prevCasingFinalMDValue,
                    frontParentUuid: frontParentUuid ?? undefined
                })
            }
            if (data.entityType == 'RIG_TABLE') {
                const frontParentUuid = tempInterventions.find(
                    intervention => intervention?.nodeId && content?.parent && intervention.nodeId === content.parent
                )?.frontUuid
                tempRigTables.push({
                    ...content,
                    frontParentUuid: frontParentUuid ?? undefined
                })
            }
            if (data.entityType == 'RUN') {
                const frontParentUuid = tempSections.find(
                    section => section?.nodeId && content?.parent && section.nodeId === content.parent
                )?.frontUuid
                tempRuns.push({
                    ...content,
                    startMD: content.startMDValue,
                    finalMD: content.finalMDValue,
                    frontParentUuid: frontParentUuid ?? undefined
                })
            }
            if (data.entityType == 'CEMENT_PLUG') {
                const frontParentUuid = tempRuns.find(
                    run => run?.nodeId && content?.parent && run.nodeId === content.parent
                )?.frontUuid
                tempCements.push({
                    ...content,
                    startMD: content.startMDValue,
                    finalMD: content.finalMDValue,
                    frontParentUuid: frontParentUuid ?? undefined
                })
            }
            if (data.entityType == 'TUBULAR_TYPE') {
                const frontParentUuid = tempRuns.find(
                    run => run?.nodeId && content?.parent && run.nodeId === content.parent
                )?.frontUuid
                tempTubulars.push({
                    ...content,
                    startMD: content.startMDValue,
                    finalMD: content.finalMDValue,
                    oD: content.oDValue,
                    frontParentUuid: frontParentUuid ?? undefined
                })
            }
            if (data.entityType == 'TUBULAR_RESTRICTED_PIPE_MOVEMENT') {
                const frontParentUuid = tempTubulars.find(
                    tubular => tubular?.nodeId && content?.parent && tubular.nodeId === content.parent
                )?.frontUuid
                tempPipeMovements.push({
                    ...content,
                    startMD: content.startMDValue,
                    finalMD: content.finalMDValue,
                    frontParentUuid: frontParentUuid ?? undefined,
                    restrictedPipeMovementSpeed: content?.restrictedPipeMovementSpeed ?? {
                        value: content?.restrictedPipeMovementSpeedValue,
                        name: content?.restrictedPipeMovementSpeedUom,
                        id: 0
                    }
                })
            }
            if (data.entityType == 'TUBULAR_RESTRICTED_CONNECTION') {
                const frontParentUuid = tempTubulars.find(
                    tubular => tubular?.nodeId && content?.parent && tubular.nodeId === content.parent
                )?.frontUuid
                tempConnections.push({
                    ...content,
                    startMD: content.startMDValue,
                    finalMD: content.finalMDValue,
                    frontParentUuid: frontParentUuid ?? undefined,
                    restrictedTimeConnection: content?.restrictedTimeConnection ?? {
                        value: content?.restrictedConnectionTypeValue ?? content?.restrictedTimeConnectionValue,
                        name: content?.restrictedConnectionTypeUom ?? content?.restrictedTimeConnectionUom,
                        id: 0
                    }
                })
            }
            if (data.entityType == 'TUBULAR_RESTRICTED_ROP') {
                const frontParentUuid = tempTubulars.find(
                    tubular => tubular?.nodeId && content?.parent && tubular.nodeId === content.parent
                )?.frontUuid
                tempRops.push({
                    ...content,
                    startMD: content.startMDValue,
                    finalMD: content.finalMDValue,
                    frontParentUuid: frontParentUuid ?? undefined,
                    restrictedRopSpeed: content?.restrictedRopSpeed ?? {
                        value: content?.restrictedRopSpeedValue,
                        name: content?.restrictedRopSpeedUom,
                        id: 0
                    }
                })
            }
        })

        setData.setWells(tempWells)
        setData.setInterventions(tempInterventions)
        setData.setSections(tempSections)
        setData.setRigTables(tempRigTables)
        setData.setRuns(tempRuns)
        setData.setCements(tempCements)
        setData.setTubulars(tempTubulars)
        setData.setPipeMovements(tempPipeMovements)
        setData.setConnections(tempConnections)
        setData.setRops(tempRops)

        setIsFetching(false)
    }

    useEffect(() => {
        mountDataTable(temporaryData, allCodafWell, allCodafRig, liveWells, liveRigs)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [temporaryData, allCodafWell, allCodafRig, liveWells, liveRigs])

    useEffect(() => {
        fetchAllTemporaryData()
            .then(data => {
                setIsFetching(true)
                if ((data as any)?.body) {
                    setTemporaryData([])

                    return
                }
                setTemporaryData(data)
            })
            .finally(() => setIsFetching(false))
        fetchCodafEntity('well').then(wells => setAllCodafWell(wells))
        fetchCodafEntity('rig').then(rigs => setAllCodafRig(rigs))
        fetchWells().then(data => setLiveWells(data))
        fetchRigs().then(data => setLiveRigs(data))
        fetchEntityOptions('well').then(data => setOptionsOfWellEntity(data))
    }, [])

    return { data, setData, liveRigs, liveWells, isFetching, setIsFetching, optionsOfWellEntity }
}
